import { useNewCode } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { SettingsIcon } from '@calendly/ui/components/icons';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import ScopeDropdown from 'components/home_bar/scope_dropdown';
import { MeetingsFilter } from 'components/meetings';
import { hadCalendlyAllTrialSelector, isAvailableFeatureSelector, isMIFeatureAvailableSelector } from 'features/billing';
import { useGetMeetingIntelligenceUserSettingsQuery, StepTwo, MI_MEETINGS_TOUR, handleCheckStepEnabled, NotetakerSettingsButtonWithSpotlightWrapper } from 'features/meeting_intelligence';
import { useFilters } from 'hooks/meetings/filters';
import { nowIsAfterEndOfMonthForDate } from 'shared/paywalls/now_is_after_date';
import { ExportButton } from './export_button';
import { useGetMeetingsFilterCountQuery, useGetMeetingsSupergraphFilterCountQuery } from './graphql_api';
import { createUseResultsCount } from './hooks/use_results_count';
import { PaywallBanner } from './paywalls/paywall_banner';
import { PersistentFiltersMenu } from './persistent_filters_menu';
import { SearchInput } from './search';
const headerContainerIsPhoneCss = "h1pzthai";
const headerContainerMispotlightCss = "h1ucv90c";
const headerContainerStickyCss = "h2y0gce";
const headerContainerIsPretendingCss = "h49spf8";
const headerContainerCss = "htz5rl1";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    mispotlight,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const {
    data
  } = useGetUserQuery();
  const isPretending = data?.is_pretending;
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, {
    [headerContainerStickyCss]: true,
    // Always sticky when using window scrolling (disabled on short viewports via media query)
    [headerContainerIsPhoneCss]: isPhone,
    [headerContainerIsPretendingCss]: isPretending,
    [headerContainerMispotlightCss]: mispotlight
  }, props.className)} />;
});
const searchInputContainerIsTabletCss = "s1mv259i";
const searchInputContainerCss = "s139l2lo";
const SearchInputContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(searchInputContainerCss, {
    [searchInputContainerIsTabletCss]: isTablet
  }, props.className)} />;
});
const rowIsPhoneCss = "r1ff6x6d";
const rowCss = "rmtqggu";
const Row = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(rowCss, {
    [rowIsPhoneCss]: isPhone
  }, props.className)} />;
});
const filterSectionCss = "f10lcrrd";
const FilterSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(filterSectionCss, props.className)} />;
});
const ctaSectionCss = "c1vywd35";
const ctaButtonLabelCss = "cr4sdwu";
const CtaSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(ctaSectionCss, props.className)} />;
});
const MobileHeader = forwardRef(({
  scope,
  canExport,
  useResultsCount,
  showMeetingSearch,
  meetingIntelligenceEnabled,
  notetakerEnabled,
  filterDefaults,
  initialValue,
  onboardingTourSettingsRef
}, ref) => {
  const location = useLocation();
  const showStepTwo = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_TWO);
  const isFilterSettingsEnabled = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled;
  return <HeaderContainer ref={ref} mispotlight={showStepTwo}>
      <Row>
        <ScopeDropdown />
      </Row>
      <Row>
        {(showMeetingSearch || meetingIntelligenceEnabled) && <SearchInput initialValue={initialValue} filterDefaults={filterDefaults} />}
        <MeetingsFilter useResultsCount={useResultsCount} filterDefaults={filterDefaults} notetakerEnabled={notetakerEnabled} />
        {notetakerEnabled && !isFilterSettingsEnabled && <NotetakerSettingsButtonWithSpotlightWrapper as="a" href="/app/personal/ai_notetaker" aria-label={I18n.t('publisher.personal_settings.navigation.notetaker_settings')}>
            <SettingsIcon />
          </NotetakerSettingsButtonWithSpotlightWrapper>}
        {canExport && <ExportButton text={null} isPhone scope={scope} />}
        {isFilterSettingsEnabled && <PersistentFiltersMenu ref={onboardingTourSettingsRef} notetakerEnabled={notetakerEnabled} />}
      </Row>
      <StepTwo />
    </HeaderContainer>;
});
const Paywall = ({
  notetakerEnabled
}) => {
  const hadCalendlyAllTrial = useSelector(hadCalendlyAllTrialSelector);
  const featureKey = 'meeting_intelligence_nonfree';
  const isFeatureKeyAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey
  }));
  const paywallDismissedOn = window.localStorage.getItem('mi_meetings_page_paywall_dismissed_on');
  const isNotDismissedThisMonth = paywallDismissedOn ? nowIsAfterEndOfMonthForDate(paywallDismissedOn) : true;
  const showPaywall = hadCalendlyAllTrial && !isFeatureKeyAvailable && notetakerEnabled && isNotDismissedThisMonth;
  if (!showPaywall) return null;
  return <Row>
      <PaywallBanner featureKey={featureKey} />
    </Row>;
};
const DesktopHeader = forwardRef(({
  scope,
  selectedView,
  canExport,
  useResultsCount,
  clearActiveMeeting,
  notetakerEnabled,
  showMeetingSearch,
  meetingIntelligenceEnabled,
  filterDefaults,
  initialValue,
  onboardingTourSettingsRef
}, ref) => {
  const location = useLocation();
  const showStepTwo = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_TWO);
  const isMIEnabled = useNewCode('meeting_intelligence', false)?.enabled;
  const isFilterSettingsEnabled = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled;
  return <HeaderContainer ref={ref} mispotlight={showStepTwo} data-pendo-id={!isMIEnabled ? 'new-meetings-page' : undefined}>
      <Row>
        <FilterSection>
          <ScopeDropdown />
          {(showMeetingSearch || meetingIntelligenceEnabled) && <SearchInputContainer>
              <SearchInput initialValue={initialValue} filterDefaults={filterDefaults} />
            </SearchInputContainer>}
          <MeetingsFilter useResultsCount={useResultsCount} filterDefaults={filterDefaults} notetakerEnabled={notetakerEnabled} />
        </FilterSection>
        <CtaSection>
          {notetakerEnabled && !isFilterSettingsEnabled && <NotetakerSettingsButtonWithSpotlightWrapper as="a" href="/app/personal/ai_notetaker" aria-label={I18n.t('publisher.personal_settings.navigation.notetaker_settings')}>
              <SettingsIcon />
              <span className={ctaButtonLabelCss}>{I18n.t('publisher.personal_settings.navigation.notetaker_settings')}</span>
            </NotetakerSettingsButtonWithSpotlightWrapper>}
          {canExport && <ExportButton text={I18n.t('publisher.meetings.export')} isPhone={false} scope={scope} />}
          {isFilterSettingsEnabled && <PersistentFiltersMenu ref={onboardingTourSettingsRef} notetakerEnabled={notetakerEnabled} />}
        </CtaSection>
      </Row>
      <StepTwo />
      <Paywall notetakerEnabled={notetakerEnabled} />
    </HeaderContainer>;
});
export const SearchFilterExport = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  const useResultsCount = createUseResultsCount(useGetMeetingsFilterCountQuery, useGetMeetingsSupergraphFilterCountQuery);
  const {
    data: meetingIntelligenceUserData
  } = useGetMeetingIntelligenceUserSettingsQuery();
  const isMIBillingFeatureAvailable = useSelector(isMIFeatureAvailableSelector);
  const meetingIntelligenceEnabled = useNewCode('meeting_intelligence')?.enabled;
  const notetakerAvailable = !!meetingIntelligenceUserData?.miEnabled || !!isMIBillingFeatureAvailable && meetingIntelligenceEnabled;
  const showMeetingSearch = useNewCode('scheduling_meetings_page_search')?.enabled;
  const {
    getFilterParams
  } = useFilters();
  const filterParams = getFilterParams();
  const commonProps = {
    ref,
    useResultsCount,
    notetakerEnabled: notetakerAvailable,
    showMeetingSearch,
    meetingIntelligenceEnabled,
    initialValue: filterParams.search_keyword,
    ...props
  };
  return isPhone ? <MobileHeader {...commonProps} /> : <DesktopHeader {...commonProps} />;
});

require("./search_filter_export.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./search_filter_export.js");